
import { useState } from 'react';

export default function ETDPProgrammesSection() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedProgramme, setSelectedProgramme] = useState('assessor');

  const programmes = {
    assessor: {
      title: 'Assessor Programme',
      nqfLevel: 'NQF 5',
      credits: '15 Credits',
      duration: '1 month',
      description: 'Develop competencies to assess learner achievements against unit standards and qualifications',
      unitStandards: [
        'US 115753: Conduct outcome-based assessments'
      ],
      prerequisites: 'Relevant qualification in field of assessment + 2 years experience',
      certification: 'ETDP SETA Assessor Certificate'
    },
    moderator: {
      title: 'Moderator Programme',
      nqfLevel: 'NQF 6',
      credits: '10 Credits',
      duration: '1 month',
      description: 'Develop competencies to moderate assessment practices and ensure quality assurance',
      unitStandards: [
        'US 115759: Conduct moderation of outcome-based assessments'
      ],
      prerequisites: 'Assessor qualification + 3 years assessment experience',
      certification: 'ETDP SETA Moderator Certificate'
    },
    facilitator: {
      title: 'Facilitator Programme',
      nqfLevel: 'NQF 5',
      credits: '10 Credits',
      duration: '1 month',
      description: 'Develop competencies to facilitate learning using a variety of methodologies',
      unitStandards: [
        'US 117871: Facilitate learning using a variety of given methodologies'
      ],
      prerequisites: 'Relevant qualification + 2 years training experience',
      certification: 'ETDP SETA Facilitator Certificate'
    }
  };

  const programmeOptions = [
    { id: 'assessor', name: 'Assessor Programme' },
    { id: 'moderator', name: 'Moderator Programme' },
    { id: 'facilitator', name: 'Facilitator Programme' }
  ];

  const currentProgramme = programmes[selectedProgramme as keyof typeof programmes];

  return (
    <div className="mb-12">
      <div 
        className="bg-white rounded-lg shadow-md border border-gray-200 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">
              ETDP Programmes
            </h2>
            <p className="text-gray-600">
              Education, Training and Development Practices qualifications
            </p>
          </div>
          <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-2xl text-[#2D7F3E]`}></i>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6">
            {/* Programme Selection */}
            <div className="flex flex-wrap gap-2 mb-6">
              {programmeOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setSelectedProgramme(option.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                    selectedProgramme === option.id
                      ? 'bg-[#2D7F3E] text-white'
                      : 'bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#E0E0E0]'
                  }`}
                >
                  {option.name}
                </button>
              ))}
            </div>

            {/* Programme Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4">
                  {currentProgramme.title}
                </h3>
                
                <p className="text-gray-600 mb-6">
                  {currentProgramme.description}
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-500">NQF Level:</span>
                    <span className="font-medium">{currentProgramme.nqfLevel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Credits:</span>
                    <span className="font-medium">{currentProgramme.credits}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Duration:</span>
                    <span className="font-medium">{currentProgramme.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Certification:</span>
                    <span className="font-medium text-[#2D7F3E]">{currentProgramme.certification}</span>
                  </div>
                </div>

                <div className="bg-[#F5F5F5] p-4 rounded-lg mb-6">
                  <h4 className="font-medium text-[#1A1A1A] mb-2">Prerequisites:</h4>
                  <p className="text-sm text-gray-600">{currentProgramme.prerequisites}</p>
                </div>

                <div className="flex gap-3">
                  <button className="btn btn-primary">
                    Apply Now
                  </button>
                  <button className="border border-[#2D7F3E] text-[#2D7F3E] px-6 py-3 rounded-lg hover:bg-[#2D7F3E] hover:text-white transition-colors font-medium whitespace-nowrap">
                    Download Brochure
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-[#1A1A1A] mb-4">Unit Standards Covered:</h4>
                <div className="space-y-3">
                  {currentProgramme.unitStandards.map((standard, index) => (
                    <div key={index} className="flex items-start p-3 bg-[#F9F9F9] rounded-lg">
                      <div className="w-8 h-8 bg-[#2D7F3E] text-white rounded-full flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm text-[#1A1A1A] font-medium">
                          {standard.split(':')[0]}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {standard.split(':')[1]}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-[#E8F5E8] rounded-lg">
                  <div className="flex items-center mb-2">
                    <i className="ri-award-line text-[#2D7F3E] mr-2"></i>
                    <span className="font-medium text-[#1A1A1A]">ETDP SETA Accredited</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    This programme is fully accredited by the Education, Training and Development Practices SETA.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
